// Busca ao montar o header, ao abrir o sino e a cada 60 s (D-N2).
export const INTERVALO_ATUALIZACAO_NOTIFICACOES_MS = 60000;

// O popper do sino mostra no maximo 8; o restante fica na pagina de listagem.
export const MAX_NOTIFICACOES_NO_SINO = 8;

// Nao ha endpoint em lote: "marcar todas" e um PUT por notificacao. Ate
// LIMITE_SEM_LOTE vao todos juntos (Promise.allSettled); acima disso, em lotes
// sequenciais de TAMANHO_LOTE_MARCACAO para nao disparar dezenas de requests
// simultaneos (D-N4).
export const TAMANHO_LOTE_MARCACAO = 5;
export const LIMITE_SEM_LOTE = 20;

// A API devolve um array puro, sem envelope; qualquer outra forma vira lista vazia.
export const extrairNotificacoes = (resposta) =>
  Array.isArray(resposta) ? resposta : [];

const ROTA_DASHBOARD = "/dashboard/resumo";
const ROTA_APROVAR_NORMATIVA = "/normativos/aprovar";

// Base so para o parser resolver caminhos relativos; o host nunca sai daqui.
const ORIGEM_FICTICIA = "http://localhost";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Caminho do proprio app: comeca com uma barra so. Duas barras, ou barra e
// contrabarra, formam uma URL protocol-relative (o parser a trata como outro
// host), nao um caminho.
const ehCaminhoRelativo = (texto) =>
  texto.startsWith("/") && !/^\/[/\\]/.test(texto);

const lerUrl = (texto) => {
  try {
    if (ehCaminhoRelativo(texto)) return new URL(texto, ORIGEM_FICTICIA);

    const url = new URL(texto);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
};

// O `link` da API e uma URL ABSOLUTA do front de homologacao
// (https://app.egrc.homologacao.com.br/...). Usa-la direto (window.location ou
// <a href>) levaria quem esta em localhost:4000 para a homologacao e ignoraria
// o basename do app. Por isso o host e descartado: so `pathname + search`
// importam, e o resultado serve para navigate().
//
// Lista permitida (secao 10.B do plano): so devolve rota o que existe em
// MainRoutes.js e foi confirmado na sondagem. Tudo o mais vira null e a tela
// esconde o botao "Abrir".
//
// `/normativos/<uuid>` (4 das 10 notificacoes reais) nao e rota do front, mas
// abre a normativa: /normativos/aprovar e /normativas/criar renderizam o mesmo
// NovaNormativa, que carrega o registro pelo `?idnormative=` e decide o modo
// pelo registro, nao pelo caminho (conferido em 2026-10-14). Vai para a rota de
// aprovacao porque /normativas/criar aberta sem state rotula o breadcrumb como
// "Nova Normativa".
//
// O resultado e sempre uma de tres formas: null, "/dashboard/resumo" ou
// "/normativos/aprovar?idnormative=<uuid em minusculas>" (como o backend
// devolve os ids). Parametros extras do link sao descartados.
export const rotaDaNotificacao = (link) => {
  if (typeof link !== "string") return null;

  const url = lerUrl(link.trim());
  if (!url) return null;

  if (url.pathname === ROTA_DASHBOARD) return ROTA_DASHBOARD;

  if (url.pathname === ROTA_APROVAR_NORMATIVA) {
    const id =
      url.searchParams.get("idnormative") ??
      url.searchParams.get("idNormative");
    return UUID.test(id ?? "")
      ? `${ROTA_APROVAR_NORMATIVA}?idnormative=${id.toLowerCase()}`
      : null;
  }

  const normativo = url.pathname.match(/^\/normativos\/([^/]+)$/);
  if (normativo && UUID.test(normativo[1])) {
    return `${ROTA_APROVAR_NORMATIVA}?idnormative=${normativo[1].toLowerCase()}`;
  }

  return null;
};

const textoOuVazio = (valor) => (typeof valor === "string" ? valor : "");

// Converte UM item de `GET notifications` (ou o objeto de `GET
// notifications/{id}`) no modelo que o sino ja usa. A API so tem cinco campos
// (idNotification, title, message, isRead, link): sem data, sem tipo e sem
// destinatario. Os campos do modelo sem fonte saem vazios (`detalhe`, `hora`,
// `criadaEm`) e o componente omite a linha secundaria e a hora. `tipo` fica
// null e `tom` fixo: `title` e texto livre e nao deve virar tipo.
// Devolve apenas dados (sem JSX); o icone e escolhido no componente.
export const mapearNotificacao = (api) => {
  if (!api || typeof api !== "object" || Array.isArray(api)) return null;

  const { idNotification, title, message, isRead, link } = api;
  if (typeof idNotification !== "string" || !idNotification.trim()) {
    return null;
  }

  return {
    id: idNotification,
    tom: "primary",
    tipo: null,
    destaque: textoOuVazio(title),
    texto: textoOuVazio(message),
    detalhe: "",
    hora: "",
    // Qualquer coisa que nao seja o booleano true (ausente, "true" em string)
    // conta como nao lida: na duvida, o badge acusa e o usuario confere.
    lida: isRead === true,
    criadaEm: null,
    link: typeof link === "string" ? link : null,
    rota: rotaDaNotificacao(link),
  };
};

const comoLista = (lista) => (Array.isArray(lista) ? lista : []);

// Duas passadas (e nao `sort`) para a estabilidade valer por construcao: nao
// lidas primeiro e, dentro de cada grupo, a ordem recebida. A API nao tem data
// nem ORDER BY, entao "mais recente" nao existe; a ordem da API e arbitraria e
// uma notificacao nova pode cair em qualquer posicao. Nao lidas na frente
// garantem que ela nao fique fora do popper do sino.
export const ordenarNaoLidasPrimeiro = (lista) => {
  const itens = comoLista(lista);

  return [...itens.filter((n) => !n.lida), ...itens.filter((n) => n.lida)];
};

// Total do badge do sino: conta TODAS as nao lidas da lista, nao so as 8 que
// o popper exibe.
export const contarNaoLidas = (lista) =>
  comoLista(lista).filter((n) => !n.lida).length;

// Entrada do "marcar todas como lidas".
export const idsNaoLidas = (lista) =>
  comoLista(lista)
    .filter((n) => !n.lida)
    .map((n) => n.id);

// Imutavel: so o item com o id informado e recriado; os demais mantem a mesma
// referencia. Base da atualizacao otimista (aplicar antes do PUT) e do
// rollback (reaplicar o valor anterior se o PUT falhar).
export const aplicarLeitura = (lista, id, lida) =>
  comoLista(lista).map((n) =>
    n.id === id ? { ...n, lida: lida === true } : n,
  );

// Ponto de entrada do hook: resposta crua do GET -> lista pronta para a tela
// (itens sem id descartados, nao lidas primeiro).
export const mapearNotificacoes = (resposta) =>
  ordenarNaoLidasPrimeiro(
    extrairNotificacoes(resposta)
      .map(mapearNotificacao)
      .filter((n) => n !== null),
  );

// Mesma normalizacao do `fuzzyFilter` das listagens (ex.: ListaControles) e do
// `normalizeSearchText` de responsibleAutocomplete: sem acento, sem caixa e sem
// espacos nas pontas. Nenhuma das duas e exportada (ficam dentro do componente
// ou do modulo), e a convencao do projeto e replicar em vez de compartilhar.
const normalizarTexto = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const SITUACOES = new Map([
  ["naoLidas", (n) => !n.lida],
  ["lidas", (n) => n.lida],
]);

// A API nao filtra (IsRead e qualquer outro parametro sao ignorados), entao a
// pagina filtra no cliente. `situacao`: "todas" | "naoLidas" | "lidas" (valor
// desconhecido = todas). `termo` busca no destaque e no texto; os dois entram
// numa mesma string para a busca poder atravessar o titulo e a mensagem
// ("normativo verifique").
export const filtrarNotificacoes = (lista, filtros) => {
  const { situacao, termo } = filtros ?? {};
  const naSituacao = SITUACOES.get(situacao) ?? (() => true);
  const busca = normalizarTexto(termo);

  return comoLista(lista).filter(
    (n) =>
      naSituacao(n) &&
      (busca === "" ||
        normalizarTexto(`${n.destaque} ${n.texto}`).includes(busca)),
  );
};

// Fatia os ids em lotes sequenciais. So se usa quando ids.length >
// LIMITE_SEM_LOTE; abaixo disso todos os PUTs vao de uma vez. Um tamanho
// invalido (0, negativo, fracionario) cairia num laco infinito ou num lote
// vazio, entao volta ao padrao.
export const dividirEmLotes = (ids, tamanho = TAMANHO_LOTE_MARCACAO) => {
  const passo =
    Number.isInteger(tamanho) && tamanho > 0 ? tamanho : TAMANHO_LOTE_MARCACAO;
  const itens = comoLista(ids);
  const lotes = [];

  for (let inicio = 0; inicio < itens.length; inicio += passo) {
    lotes.push(itens.slice(inicio, inicio + passo));
  }

  return lotes;
};

// Recebe o array de Promise.allSettled (de um lote ou de varios lotes
// concatenados) e resume para a tela. `mensagem` e null quando tudo deu certo;
// o chamador so mostra snackbar quando ela existe.
export const resumirResultadoEmLote = (resultados) => {
  const itens = comoLista(resultados);
  const total = itens.length;
  const ok = itens.filter((r) => r?.status === "fulfilled").length;
  const falhas = total - ok;

  let mensagem = null;
  if (falhas > 0 && ok === 0) {
    mensagem =
      total === 1
        ? "Não foi possível marcar a notificação como lida."
        : "Não foi possível marcar as notificações como lidas.";
  } else if (falhas === 1) {
    mensagem = `1 de ${total} notificações não pôde ser marcada como lida.`;
  } else if (falhas > 1) {
    mensagem = `${falhas} de ${total} notificações não puderam ser marcadas como lidas.`;
  }

  return { total, ok, falhas, mensagem };
};

// Primeira mensagem de texto de um valor: string, objeto { code, message } ou
// lista (aninhada ou nao) desses. Vazio quando nao ha nada utilizavel.
const primeiraMensagem = (valor) => {
  if (typeof valor === "string") return valor.trim();

  if (Array.isArray(valor)) {
    for (const elemento of valor) {
      const mensagem = primeiraMensagem(elemento);
      if (mensagem) return mensagem;
    }
    return "";
  }

  if (valor && typeof valor === "object" && typeof valor.message === "string") {
    return valor.message.trim();
  }

  return "";
};

// ProblemDetails (RFC 9457): { type, title, status, errors: { campo: [msg] },
// traceId }. Nesta API ele vem do model binding/parser do .NET, com texto
// tecnico em ingles ("The value 'abc' is not valid.", "'n' is an invalid
// start..."), que nao serve para o usuario. As validacoes de negocio, em
// pt-BR, vem no envelope do Notifier. Reconhece-se por `errors` em formato de
// objeto (uma lista `errors` seria um envelope com esse nome de recurso) ou
// por `title` string com `status` numerico.
const ehProblemDetails = ({ errors, title, status }) =>
  (errors !== null && typeof errors === "object" && !Array.isArray(errors)) ||
  (typeof title === "string" && typeof status === "number");

// Extrai a mensagem de uma resposta de erro ja parseada (ou null). Esta API
// responde erro em formatos diferentes, e um id inexistente e 400 (nao 404):
//  - envelope do Notifier: { <recurso>: [ { code, message } ] }
//    -> primeira `message` nao vazia de qualquer chave cujo valor seja lista;
//  - array de strings (padrao do Engagement) -> primeira string;
//  - ProblemDetails -> `padrao` (ver ehProblemDetails).
// Qualquer outra coisa (inclusive string solta) tambem devolve `padrao`.
// Nunca lanca.
export const extrairMensagemErroApi = (corpo, padrao) => {
  if (Array.isArray(corpo)) return primeiraMensagem(corpo) || padrao;

  if (!corpo || typeof corpo !== "object" || ehProblemDetails(corpo)) {
    return padrao;
  }

  return primeiraMensagem(Object.values(corpo).filter(Array.isArray)) || padrao;
};
